import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export type DayKey = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

const DAYS: { value: DayKey; label: string }[] = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const DAY_LABELS: Record<DayKey, string> = {
  sunday: "Sunday",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
};

const INTERVAL_OPTIONS = [
  { value: "0", label: "0 min (back-to-back)" },
  { value: "15", label: "15 min" },
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "60 min" },
  { value: "90", label: "90 min" },
  { value: "120", label: "120 min" },
];

const EDITABLE_DURATION_OPTIONS = [
  { value: "15 min", label: "15 min" },
  { value: "30 min", label: "30 min" },
  { value: "45 min", label: "45 min" },
  { value: "60 min", label: "60 min" },
  { value: "90 min", label: "90 min" },
  { value: "120 min", label: "120 min" },
];

/**
 * Parse a duration string like "30 min", "1 hr", "1 hr 30 min" into total minutes.
 * Returns null if it can't be parsed.
 */
const parseDurationToMinutes = (duration: string): number | null => {
  if (!duration || !duration.trim()) return null;
  const hrMatch = duration.match(/(\d+)\s*hr/i);
  const minMatch = duration.match(/(\d+)\s*min/i);
  const hours = hrMatch ? parseInt(hrMatch[1]) : 0;
  const minutes = minMatch ? parseInt(minMatch[1]) : 0;
  const total = hours * 60 + minutes;
  return total > 0 ? total : null;
};

const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const minutesToHHMM = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

interface BulkCreateSlotsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listingDuration: string;
  currentWeeklySchedule: Record<string, string[]>;
  onApply: (day: DayKey, newSlots: string[], durationValue?: string) => void;
  /** "read-only" shows the listing duration as static text (admin edit dialog).
   *  "editable" renders a Select for duration (onboarding, where no listing exists yet). */
  durationMode?: "read-only" | "editable";
}

const BulkCreateSlotsModal = ({
  open,
  onOpenChange,
  listingDuration,
  currentWeeklySchedule,
  onApply,
  durationMode = "read-only",
}: BulkCreateSlotsModalProps) => {
  const [selectedDay, setSelectedDay] = useState<DayKey>("monday");
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("20:00");
  const [interval, setInterval] = useState("30");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Editable duration. Only used when durationMode === "editable"
  const [editableDuration, setEditableDuration] = useState(
    listingDuration && EDITABLE_DURATION_OPTIONS.some(o => o.value === listingDuration)
      ? listingDuration
      : "60 min"
  );

  // Keep editable duration in sync when listingDuration prop changes (e.g. modal re-opens)
  useEffect(() => {
    if (durationMode === "editable" && listingDuration) {
      const match = EDITABLE_DURATION_OPTIONS.find(o => o.value === listingDuration);
      if (match) setEditableDuration(match.value);
    }
  }, [listingDuration, durationMode]);

  const activeDuration = durationMode === "editable" ? editableDuration : listingDuration;
  const durationMinutes = useMemo(() => parseDurationToMinutes(activeDuration), [activeDuration]);
  const noDuration = durationMinutes === null;

  const startMinutes = useMemo(() => timeToMinutes(startTime), [startTime]);
  const endMinutes = useMemo(() => timeToMinutes(endTime), [endTime]);
  const intervalMinutes = parseInt(interval);

  const timeRangeValid = endMinutes > startMinutes;

  const generatedSlots = useMemo(() => {
    if (noDuration || !timeRangeValid) return [];
    const cycleMinutes = durationMinutes + intervalMinutes;
    const slots: string[] = [];
    let cursor = startMinutes;
    while (cursor + durationMinutes <= endMinutes) {
      slots.push(minutesToHHMM(cursor));
      cursor += cycleMinutes;
    }
    return slots;
  }, [noDuration, durationMinutes, intervalMinutes, startMinutes, endMinutes, timeRangeValid]);

  const canGenerate = !noDuration && timeRangeValid && generatedSlots.length > 0;

  const handleGenerate = () => {
    if (!canGenerate) return;
    const existingSlots = currentWeeklySchedule[selectedDay] || [];
    if (existingSlots.length > 0) {
      setConfirmOpen(true);
    } else {
      applySlots();
    }
  };

  const applySlots = () => {
    onApply(
      selectedDay,
      generatedSlots,
      durationMode === "editable" ? editableDuration : undefined
    );
    toast.success(`Generated ${generatedSlots.length} slots for ${DAY_LABELS[selectedDay]}.`);
    setConfirmOpen(false);
    onOpenChange(false);
  };

  const existingCount = (currentWeeklySchedule[selectedDay] || []).length;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk create time slots</DialogTitle>
            <DialogDescription>
              Generate multiple time slots at once for a day of the week.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Day of week */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Day of week</Label>
              <Select value={selectedDay} onValueChange={(v) => setSelectedDay(v as DayKey)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Service duration */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Service duration</Label>
              {durationMode === "editable" ? (
                <>
                  <Select value={editableDuration} onValueChange={setEditableDuration}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EDITABLE_DURATION_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Duration applies to all slots for this listing.
                  </p>
                </>
              ) : (
                <>
                  <div className="px-3 py-2 border rounded-md bg-muted/50 text-sm font-medium">
                    {noDuration ? (
                      <span className="text-destructive">No duration set</span>
                    ) : (
                      listingDuration
                    )}
                  </div>
                  {noDuration ? (
                    <p className="text-xs text-destructive">
                      This listing has no duration set. Please set a duration first.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      To change duration, edit the listing's details.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Time range */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Time range</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Start time</label>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">End time</label>
                  <Input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>
              {!timeRangeValid && startTime && endTime && (
                <p className="text-xs text-destructive">
                  End time must be after start time.
                </p>
              )}
            </div>

            {/* Interval */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Time interval between slots</Label>
              <Select value={interval} onValueChange={setInterval}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVAL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">(the gap after each slot ends)</p>
            </div>

            {/* Preview / status */}
            {canGenerate && (
              <div className="rounded-md bg-primary/5 border border-primary/20 px-3 py-2">
                <p className="text-sm text-primary font-medium">
                  {generatedSlots.length} slot{generatedSlots.length !== 1 ? "s" : ""} will be
                  generated ({generatedSlots[0]} – {generatedSlots[generatedSlots.length - 1]})
                </p>
              </div>
            )}
            {!noDuration && timeRangeValid && generatedSlots.length === 0 && (
              <p className="text-xs text-destructive">
                Time range is too short for even one slot with the current duration.
              </p>
            )}
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={!canGenerate}
            >
              Generate slots
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm replacement dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace existing slots?</AlertDialogTitle>
            <AlertDialogDescription>
              {DAY_LABELS[selectedDay]} already has {existingCount} slot
              {existingCount !== 1 ? "s" : ""}. Replace them with the new bulk-generated slots?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={applySlots}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BulkCreateSlotsModal;
